<template>
    <LineChart
        v-if="chartData && chartOptions"
        class="chart"
        :data="chartData"
        :options="chartOptions"
    />
</template>

<script>
import 'chartjs-adapter-date-fns';
import {
    Chart as ChartJS,
    Legend,
    LineElement,
    LinearScale,
    PointElement,
    TimeScale,
    Title,
    Tooltip,
} from 'chart.js'
import { Line } from 'vue-chartjs'
import {de} from 'date-fns/locale';
import { format } from '@legacy/js/date/date-fns.js';

ChartJS.register(
    TimeScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
)

export default {
    name: 'UtilisationChart',
    components: { LineChart: Line },
    props: {
        data: {
            type: Array,
            required: true
        },
        stacked: {
            type: Boolean,
            default: false
        },
        metadata: {
            type: Object,
            required: false
        },
        selectedTimeBaseOptionValue: {
            type: String,
            default: 'daily'
        },
        selectedGroupIndexes: Set,
        selectedValueKeys: Set
    },

    computed: {
        dateFormat() {
            return this.selectedTimeBaseOptionValue === 'daily' ?
                'dd.MM.yyyy' : 'dd.MM.yyyy HH:mm "Uhr"';
        },

        chartOptions() {
            const vm = this

            return {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    intersect: true,
                    mode: 'index',
                },
                plugins: {
                    tooltip: {
                        callbacks: {
                            title(tooltipItems) {
                                const tooltipItem = tooltipItems?.[0]
                                const { parsed } = tooltipItem
                                return format(parsed.x, vm.dateFormat);
                            },
                            label(tooltipItem) {
                                const count = tooltipItem.parsed.y
                                const max = tooltipItem.dataset.maxChildren;
                                const percent = ((count*100)/max).toFixed() + '%';
                                return tooltipItem.dataset.label  + ' ' + percent + ' (' + count + '/' + max + ')';
                            },
                            footer(tooltipItems) {
                                let sum = 0;
                                tooltipItems.forEach(function(tooltipItem) {
                                    sum += tooltipItem.parsed.y;
                                });
                                return 'Gesamt: ' + sum;
                            },
                        }
                    },
                    legend: {
                        display: false,
                    },
                },
                adapters: {
                    date: {
                        locale: de
                    }
                },
                scales: {
                    y: {
                        stacked: vm.stacked,
                        ticks: {
                            beginAtZero: true,
                        },
                    },
                    x: {
                        type: 'time',
                        time: {
                            unit: 'day',
                            displayFormats: {
                                day: 'dd.MM.yyyy',
                            }
                        },
                    }
                },
                animation: {
                    duration: 500,
                },
            }
        },

        chartData() {
            let chartData = {
                labels: [],
                datasets: [],
            };

            let selectedGroups = this.data.filter((valuesByGroup, index) => {return this.selectedGroupIndexes.has(index)});

            selectedGroups.forEach((groupDataSet, index) => {
                let plotsForGroup = {};

                //initialize plot array for each selected data key
                this.selectedValueKeys.forEach((key) => {plotsForGroup[key] = []});

                //fill plot data arrays
                groupDataSet.data.forEach((valueObject) => {
                    //grab date values once for labels
                    if(index === 0) {
                        chartData.labels.push(valueObject.date);
                    }

                    this.selectedValueKeys.forEach((key) => {
                        plotsForGroup[key].push({
                            x: valueObject.date,
                            y: valueObject[key] !== undefined ? valueObject[key] : 0
                        });
                    });
                });

                //now create the chartjs objects
                this.selectedValueKeys.forEach((key) => {
                    chartData.datasets.push({
                        label: groupDataSet.generalInformation.name + ' (' + this.metadata[key].translation + ')',
                        backgroundColor: groupDataSet.generalInformation.color,
                        fill: this.stacked,
                        borderColor: this.metadata[key].color,
                        data: plotsForGroup[key],
                        maxChildren: groupDataSet.generalInformation.maxChildren,
                        pointRadius: 5,
                        pointHoverRadius: 7,
                    });
                });
            });

            return chartData;
        }
    },
}
</script>

<style scoped>
    .chart {
        min-height:400px;
    }
</style>
